/** Account page model layer — pure state-shaping functions.
 *
 * Layer: `model` per docs/design/spa-account-extraction-260524.md §3
 * and tools/lint/lint-frontend-layer-imports.py SPA_ACCOUNT_LAYERS.
 *
 * No DOM access, no fetch, no window.X — pure projectors only.
 * Extracted from web/static/account.html:412–427 in Phase AC.3.
 */
/**
 * Pure projector: derives the view-ready AccountState from /api/account response.
 * Decision tree (priority order):
 *   !user → anonymous
 *   any group unlimited_mode='invoiced' → institutional-invoiced
 *   user.has_cap_hit → cap-hit
 *   any group unlimited_mode='flat' → institutional-flat
 *   else → individual
 */
export function pickAccountState(data) {
    if (!data || !data.user)
        return 'anonymous';
    const groups = Array.isArray(data.groups) ? data.groups : [];
    const hasInvoiced = groups.some(g => g != null && g.unlimited_mode === 'invoiced');
    if (hasInvoiced)
        return 'institutional-invoiced';
    if (data.user.has_cap_hit)
        return 'cap-hit';
    const hasFlat = groups.some(g => g != null && g.unlimited_mode === 'flat');
    if (hasFlat)
        return 'institutional-flat';
    return 'individual';
}
/**
 * Returns true when the user is root admin or granted admin of any group.
 * Used to show/hide the group-admin CTA section.
 */
export function userAdminsAnyGroup(data) {
    const groups = Array.isArray(data.groups) ? data.groups : [];
    return groups.some(g => g != null && (g.is_root_admin || g.has_change_member_quotas || g.has_buy_tokens));
}
/**
 * Returns the user's personal (type='individual') group entry, or null if
 * none exists. Used by view-level renderers to read the personal credit
 * balance without duplicating the find logic.
 */
export function pickPersonalGroup(data) {
    const groups = Array.isArray(data.groups) ? data.groups : [];
    return groups.find(g => g != null && g.type === 'individual') ?? null;
}
/**
 * Reads the credit balance from a group object, falling back to the legacy
 * user-level field for backward-compat with API responses that predate the
 * groups schema.
 */
export function creditBalanceOf(group, fallbackUser) {
    const g = group;
    if (g != null && typeof g.credit_balance === 'number')
        return g.credit_balance;
    const u = fallbackUser;
    if (u != null && typeof u.credit_balance === 'number')
        return u.credit_balance;
    return null;
}
